import React, { useState } from 'react';
import { Book, Server, Wifi, Terminal, AlertCircle, CheckCircle, Copy, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const RADIUSGuide = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [rosVersion, setRosVersion] = useState('ros7'); // ros6 or ros7

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const CodeBlock = ({ code, language = 'bash' }) => (
    <div className="relative bg-gray-900 text-gray-100 rounded-lg p-4 my-4">
      <button
        onClick={() => copyToClipboard(code)}
        className="absolute top-2 right-2 p-2 hover:bg-gray-700 rounded"
      >
        <Copy className="w-4 h-4" />
      </button>
      <pre className="overflow-x-auto">
        <code className={`language-${language}`}>{code}</code>
      </pre>
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen dark:bg-slate-900">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 dark:text-slate-100">
              📚 Panduan RADIUS Setup
            </h1>
            <p className="text-gray-600 dark:text-slate-300">
              Setup Mikrotik dengan RADIUS untuk router tanpa IP Publik (via L2TP VPN)
            </p>
          </div>
          
          {/* RouterOS Version Selector */}
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-2 dark:bg-slate-800 dark:border-slate-700">
            <span className="text-sm font-medium text-gray-700 dark:text-slate-200">RouterOS Version:</span>
            <div className="flex gap-2">
              <button
                onClick={() => setRosVersion('ros6')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  rosVersion === 'ros6'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                ROS 6.x
              </button>
              <button
                onClick={() => setRosVersion('ros7')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  rosVersion === 'ros7'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                ROS 7.x
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto dark:border-slate-700">
        {[
          { id: 'overview', label: '🎯 Overview', icon: Book },
          { id: 'server-setup', label: '🖥️ Server Setup', icon: Server },
          { id: 'mikrotik-vpn', label: '🔗 Mikrotik VPN', icon: Wifi },
          { id: 'mikrotik-radius', label: '📡 RADIUS Config', icon: Terminal },
          { id: 'troubleshooting', label: '🔧 Troubleshooting', icon: AlertCircle }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-indigo-600 text-indigo-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 dark:bg-slate-800 dark:border-slate-700">
        {activeTab === 'overview' && <OverviewContent CodeBlock={CodeBlock} rosVersion={rosVersion} />}
        {activeTab === 'server-setup' && <ServerSetupContent CodeBlock={CodeBlock} />}
        {activeTab === 'mikrotik-vpn' && <MikrotikVPNContent CodeBlock={CodeBlock} rosVersion={rosVersion} />}
        {activeTab === 'mikrotik-radius' && <MikrotikRADIUSContent CodeBlock={CodeBlock} rosVersion={rosVersion} />}
        {activeTab === 'troubleshooting' && <TroubleshootingContent CodeBlock={CodeBlock} rosVersion={rosVersion} />}
      </div>
    </div>
  );
};

// Overview Content
const OverviewContent = ({ CodeBlock, rosVersion }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold mb-4">🎯 Scenario: Router Tanpa IP Publik</h2>
      <p className="text-gray-700 mb-4 dark:text-slate-200">
        Kebanyakan ISP menggunakan router Mikrotik di lapangan yang <strong>tidak memiliki IP Publik</strong>.
        Solusinya adalah menggunakan <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/40">L2TP VPN Tunnel</Badge> ke server billing.
      </p>
      
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <Badge className={rosVersion === 'ros7' ? 'bg-indigo-600 text-white' : 'bg-blue-600 text-white'}>
            {rosVersion === 'ros7' ? 'RouterOS 7.x' : 'RouterOS 6.x'}
          </Badge>
          <span className="text-sm text-gray-700 dark:text-slate-200">
            Command dalam panduan ini disesuaikan untuk <strong>{rosVersion === 'ros7' ? 'RouterOS 7.x' : 'RouterOS 6.x'}</strong>
          </span>
        </div>
      </div>
    </div>

    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 dark:bg-blue-900/30">
      <h3 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
        <Server className="w-5 h-5" />
        Arsitektur Sistem
      </h3>
      <div className="bg-white p-4 rounded font-mono text-sm dark:bg-slate-800">
        <pre>{`
Router Mikrotik (Lapangan)          Server Billing ISP
━━━━━━━━━━━━━━━━━━━━━━              ━━━━━━━━━━━━━━━━━━━━
🏢 IP Publik: TIDAK ADA             ☁️ IP Publik: Ada
📍 IP Lokal: 192.168.x.x            📍 IP Server: 10.0.0.1
        ↓                                   ↑
    L2TP Client ←────VPN Tunnel────→ L2TP Server
        ↓                                   ↓
    📱 VPN IP: 10.10.10.2           📱 VPN IP: 10.10.10.1
        ↓                                   ↓
        └─────→ RADIUS (1812) ←────────────┘
        
👥 Customer PPPoE → 🔐 Auth via RADIUS → ✅ Internet
        `}</pre>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">✅ Keuntungan</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 dark:text-green-400" />
            <div>
              <strong>Centralized Management</strong>
              <p className="text-sm text-gray-600 dark:text-slate-300">Kelola semua router dari 1 server</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 dark:text-green-400" />
            <div>
              <strong>Full RADIUS</strong>
              <p className="text-sm text-gray-600 dark:text-slate-300">User auto-sync, bandwidth management</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 dark:text-green-400" />
            <div>
              <strong>Multi-Router</strong>
              <p className="text-sm text-gray-600 dark:text-slate-300">1 RADIUS, unlimited Mikrotik</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 dark:text-green-400" />
            <div>
              <strong>Secure</strong>
              <p className="text-sm text-gray-600 dark:text-slate-300">Encrypted VPN tunnel</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">📋 Yang Dibutuhkan</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Badge className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40">Server</Badge>
            <span>IP Publik, FreeRADIUS, L2TP Server</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/40">Mikrotik</Badge>
            <span>RouterOS v6.x atau v7.x</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40">Bandwidth</Badge>
            <span>Min. 1Mbps untuk VPN tunnel</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/40">Akses</Badge>
            <span>SSH/Winbox ke Mikrotik</span>
          </div>
        </CardContent>
      </Card>
    </div>

    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-900/30">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 dark:text-yellow-400" />
        <div>
          <h4 className="font-semibold text-yellow-900 mb-1">⚠️ Penting!</h4>
          <p className="text-sm text-yellow-800">
            Pastikan port <code className="bg-yellow-100 px-2 py-0.5 rounded dark:bg-yellow-900/40">1701/udp</code>, 
            <code className="bg-yellow-100 px-2 py-0.5 rounded mx-1 dark:bg-yellow-900/40">500/udp</code>, dan 
            <code className="bg-yellow-100 px-2 py-0.5 rounded ml-1 dark:bg-yellow-900/40">4500/udp</code> terbuka di firewall server!
          </p>
        </div>
      </div>
    </div>
  </div>
);

// Server Setup Content
const ServerSetupContent = ({ CodeBlock }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold mb-4">🖥️ Setup L2TP Server di Server Billing</h2>

    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold mb-2">1. Install L2TP Server</h3>
        <CodeBlock code={`sudo apt update
sudo apt install xl2tpd strongswan -y`} />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">2. Konfigurasi xl2tpd</h3>
        <CodeBlock code={`sudo nano /etc/xl2tpd/xl2tpd.conf`} />
        <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">Isi dengan:</p>
        <CodeBlock code={`[global]
port = 1701
access control = no

[lns default]
ip range = 10.10.10.10-10.10.10.50
local ip = 10.10.10.1
require authentication = yes
name = l2tpserver
ppp debug = yes
pppoptfile = /etc/ppp/options.xl2tpd
length bit = yes`} language="ini" />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">3. Konfigurasi PPP Options</h3>
        <CodeBlock code={`sudo nano /etc/ppp/options.xl2tpd`} />
        <CodeBlock code={`require-mschap-v2
ms-dns 8.8.8.8
ms-dns 8.8.4.4
auth
mtu 1200
mru 1200
proxyarp
lcp-echo-interval 30
lcp-echo-failure 4`} language="ini" />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">4. Buat User L2TP</h3>
        <CodeBlock code={`sudo nano /etc/ppp/chap-secrets`} />
        <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">Format:</p>
        <CodeBlock code={`# Username    Server    Password         IP
mikrotik1     *         SecretPass123    10.10.10.2
mikrotik2     *         SecretPass123    10.10.10.3`} />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">5. Enable IP Forwarding</h3>
        <CodeBlock code={`sudo nano /etc/sysctl.conf`} />
        <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">Uncomment line:</p>
        <CodeBlock code={`net.ipv4.ip_forward=1`} />
        <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">Apply:</p>
        <CodeBlock code={`sudo sysctl -p`} />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">6. Firewall Rules</h3>
        <CodeBlock code={`# Allow L2TP ports
sudo ufw allow 1701/udp
sudo ufw allow 500/udp
sudo ufw allow 4500/udp

# NAT untuk VPN subnet
sudo iptables -t nat -A POSTROUTING -s 10.10.10.0/24 -o eth0 -j MASQUERADE
sudo iptables -A FORWARD -s 10.10.10.0/24 -j ACCEPT

# Save iptables
sudo netfilter-persistent save`} />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">7. Start Services</h3>
        <CodeBlock code={`sudo systemctl restart xl2tpd
sudo systemctl enable xl2tpd
sudo systemctl restart strongswan
sudo systemctl enable strongswan

# Check status
sudo systemctl status xl2tpd`} />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">8. Allow RADIUS dari VPN</h3>
        <CodeBlock code={`sudo nano /etc/freeradius/3.0/clients.conf`} />
        <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">Tambahkan:</p>
        <CodeBlock code={`client vpn_network {
    ipaddr = 10.10.10.0/24
    secret = testing123
    shortname = vpn-clients
}`} />
        <CodeBlock code={`sudo systemctl restart freeradius`} />
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4 dark:bg-green-900/30">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 dark:text-green-400" />
          <div>
            <h4 className="font-semibold text-green-900 mb-1">✅ L2TP Server Ready!</h4>
            <p className="text-sm text-green-800">
              Server siap menerima koneksi L2TP dari router Mikrotik.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Mikrotik VPN Content
const MikrotikVPNContent = ({ CodeBlock, rosVersion }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold mb-4">🔗 Setup L2TP Client di Mikrotik</h2>
    
    <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-4 dark:bg-blue-900/30">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 dark:text-blue-400" />
        <div>
          <p className="text-sm text-blue-900">
            <strong>Catatan Penting:</strong> Command L2TP Client <strong>sama untuk ROS 6 dan ROS 7</strong>. 
            Tidak ada perubahan syntax untuk interface L2TP.
          </p>
        </div>
      </div>
    </div>

    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold mb-2">Via Terminal/SSH (Recommended)</h3>
        <CodeBlock code={`/interface l2tp-client
add connect-to=IP_PUBLIK_SERVER_BILLING \\
    name=l2tp-billing \\
    user=mikrotik1 \\
    password=SecretPass123 \\
    disabled=no \\
    add-default-route=no \\
    use-ipsec=no \\
    keepalive-timeout=60`} />
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-900/30">
        <h4 className="font-semibold text-blue-900 mb-2">📝 Penjelasan Parameter:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li><code className="bg-blue-100 px-2 py-0.5 rounded dark:bg-blue-900/40">connect-to</code> → IP Publik server billing Anda</li>
          <li><code className="bg-blue-100 px-2 py-0.5 rounded dark:bg-blue-900/40">user</code> → Username yang dibuat di /etc/ppp/chap-secrets</li>
          <li><code className="bg-blue-100 px-2 py-0.5 rounded dark:bg-blue-900/40">password</code> → Password dari chap-secrets</li>
          <li><code className="bg-blue-100 px-2 py-0.5 rounded dark:bg-blue-900/40">add-default-route=no</code> → Agar traffic internet tidak lewat VPN</li>
          <li><code className="bg-blue-100 px-2 py-0.5 rounded dark:bg-blue-900/40">keepalive-timeout=60</code> → Auto reconnect jika putus</li>
        </ul>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Cek Status Koneksi</h3>
        <CodeBlock code={`/interface l2tp-client print detail`} />
        <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">Output (jika sukses):</p>
        <CodeBlock code={`Flags: X - disabled, R - running
 0  R name="l2tp-billing" 
      connect-to=103.xxx.xxx.xxx
      user="mikrotik1"
      running=yes
      local-address=10.10.10.2
      remote-address=10.10.10.1`} language="text" />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Test Ping ke Server</h3>
        <CodeBlock code={`/tool ping 10.10.10.1 count=5`} />
        <p className="text-sm text-gray-600 dark:text-slate-300">Jika reply, berarti VPN tunnel sudah terhubung! ✅</p>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Add Route untuk RADIUS</h3>
        <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">Agar traffic ke RADIUS lewat VPN:</p>
        <CodeBlock code={`/ip route
add dst-address=10.10.10.1/32 gateway=l2tp-billing`} />
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 dark:bg-yellow-900/30">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 dark:text-yellow-400" />
          <div>
            <h4 className="font-semibold text-yellow-900 mb-1">⚠️ Via Winbox/WebFig</h4>
            <p className="text-sm text-yellow-800 mb-2">
              Jika menggunakan GUI:
            </p>
            <ol className="text-sm text-yellow-800 space-y-1 list-decimal ml-4">
              <li>Menu: <strong>Interfaces → L2TP Client</strong></li>
              <li>Klik <strong>+</strong> (Add New)</li>
              <li>Isi form sesuai parameter di atas</li>
              <li>Klik <strong>OK</strong></li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Mikrotik RADIUS Content  
const MikrotikRADIUSContent = ({ CodeBlock, rosVersion }) => {
  const ros6RADIUSCommand = `/radius
add address=10.10.10.1 \\
    secret=testing123 \\
    service=ppp,hotspot \\
    timeout=3000ms`;

  const ros7RADIUSCommand = `/radius
add address=10.10.10.1 \\
    secret=testing123 \\
    service=ppp \\
    timeout=3s`;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold mb-4">📡 Konfigurasi RADIUS di Mikrotik</h2>

      {rosVersion === 'ros7' && (
        <div className="bg-indigo-50 border-l-4 border-indigo-600 p-4 mb-4 dark:bg-indigo-900/30">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-indigo-600 mt-0.5 dark:text-indigo-400" />
            <div>
              <p className="text-sm text-indigo-900 font-semibold mb-1">
                ⚠️ RouterOS 7.x - Perubahan Command
              </p>
              <ul className="text-sm text-indigo-800 space-y-1 list-disc ml-4">
                <li><code className="bg-indigo-100 px-2 py-0.5 rounded dark:bg-indigo-900/40">timeout</code> format berubah: <code>3s</code> (bukan <code>3000ms</code>)</li>
                <li>Service <code>hotspot</code> dikonfigurasi terpisah di <code>/ip hotspot</code></li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold mb-2">1. Tambahkan RADIUS Server</h3>
          <CodeBlock code={rosVersion === 'ros7' ? ros7RADIUSCommand : ros6RADIUSCommand} />
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2 dark:bg-blue-900/30">
            <p className="text-sm text-blue-800">
              <strong>Penting:</strong> <code className="bg-blue-100 px-2 py-0.5 rounded dark:bg-blue-900/40">address=10.10.10.1</code> adalah 
              IP VPN server (BUKAN IP Publik!)
            </p>
          </div>
        </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">2. Enable RADIUS untuk PPPoE</h3>
        <CodeBlock code={`/ppp aaa
set use-radius=yes`} />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">3. Setup PPPoE Server (Full RADIUS)</h3>
        <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">Buat IP Pool:</p>
        <CodeBlock code={`/ip pool
add name=pppoe-pool ranges=10.100.1.2-10.100.1.254`} />

        <p className="text-sm text-gray-600 mb-2 mt-4 dark:text-slate-300">Buat PPP Profile:</p>
        {rosVersion === 'ros6' ? (
          <CodeBlock code={`/ppp profile
add name=pppoe-radius \\
    local-address=10.100.1.1 \\
    remote-address=pppoe-pool \\
    use-compression=default \\
    use-encryption=default \\
    only-one=yes \\
    change-tcp-mss=yes`} />
        ) : (
          <>
            <CodeBlock code={`/ppp profile
add name=pppoe-radius \\
    local-address=10.100.1.1 \\
    remote-address=pppoe-pool \\
    use-compression=default \\
    use-encryption=default \\
    only-one=yes \\
    change-tcp-mss=yes`} />
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mt-2 dark:bg-indigo-900/30">
              <p className="text-sm text-indigo-800">
                <strong>ROS 7:</strong> Syntax sama, tapi beberapa parameter deprecated. Use <code className="bg-indigo-100 px-2 py-0.5 rounded dark:bg-indigo-900/40">use-mpls</code> dan 
                <code className="bg-indigo-100 px-2 py-0.5 rounded mx-1 dark:bg-indigo-900/40">use-ipv6</code> jika perlu IPv6 support.
              </p>
            </div>
          </>
        )}

        <p className="text-sm text-gray-600 mb-2 mt-4 dark:text-slate-300">Setup PPPoE Server:</p>
        {rosVersion === 'ros6' ? (
          <CodeBlock code={`/interface pppoe-server server
add interface=ether2 \\
    service-name=ISP-SERVICE \\
    authentication=mschap2 \\
    default-profile=pppoe-radius \\
    keepalive-timeout=60 \\
    disabled=no`} />
        ) : (
          <CodeBlock code={`/interface pppoe-server server
add interface=ether2 \\
    service-name=ISP-SERVICE \\
    authentication=mschap2 \\
    default-profile=pppoe-radius \\
    keepalive-timeout=1m \\
    disabled=no`} />
        )}
        <p className="text-sm text-gray-600 mt-2 dark:text-slate-300">
          ⚠️ <strong>Ganti <code>ether2</code></strong> dengan port dimana customer terhubung!
        </p>
        {rosVersion === 'ros7' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-2 dark:bg-yellow-900/30">
            <p className="text-sm text-yellow-800">
              <strong>ROS 7 Note:</strong> <code className="bg-yellow-100 px-2 py-0.5 rounded dark:bg-yellow-900/40">keepalive-timeout</code> menggunakan 
              format waktu: <code className="bg-yellow-100 px-2 py-0.5 rounded mx-1 dark:bg-yellow-900/40">1m</code> (1 menit) bukan <code>60</code>
            </p>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">4. Test RADIUS Connection</h3>
        <CodeBlock code={`/radius monitor 0`} />
        <p className="text-sm text-gray-600 mb-2 dark:text-slate-300">Atau lihat log:</p>
        <CodeBlock code={`/log print where topics~"radius"`} />
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4 dark:bg-green-900/30">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 dark:text-green-400" />
          <div>
            <h4 className="font-semibold text-green-900 mb-1">✅ RADIUS Ready!</h4>
            <p className="text-sm text-green-800 mb-2">
              Sekarang customer bisa login PPPoE dengan username/password dari aplikasi billing.
            </p>
            <p className="text-sm text-green-800">
              Aplikasi akan otomatis sync user ke RADIUS database.
            </p>
          </div>
        </div>
      </div>

      {/* ROS 6 vs ROS 7 Differences */}
      <div className="mt-6 border-t pt-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
          Perbedaan Utama ROS 6 vs ROS 7
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-blue-200">
            <CardHeader className="bg-blue-50 dark:bg-blue-900/30">
              <CardTitle className="text-lg text-blue-900">RouterOS 6.x</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm pt-4">
              <div className="flex items-start gap-2">
                <Badge className="bg-blue-100 text-blue-800 text-xs dark:bg-blue-900/40">RADIUS</Badge>
                <code className="text-xs">timeout=3000ms</code>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-blue-100 text-blue-800 text-xs dark:bg-blue-900/40">PPPoE</Badge>
                <code className="text-xs">keepalive-timeout=60</code>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-blue-100 text-blue-800 text-xs dark:bg-blue-900/40">Service</Badge>
                <code className="text-xs">service=ppp,hotspot</code>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-green-100 text-green-800 text-xs dark:bg-green-900/40">✓</Badge>
                <span>Stabil, proven di production</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-indigo-200">
            <CardHeader className="bg-indigo-50 dark:bg-indigo-900/30">
              <CardTitle className="text-lg text-indigo-900">RouterOS 7.x</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm pt-4">
              <div className="flex items-start gap-2">
                <Badge className="bg-indigo-100 text-indigo-800 text-xs dark:bg-indigo-900/40">RADIUS</Badge>
                <code className="text-xs">timeout=3s</code>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-indigo-100 text-indigo-800 text-xs dark:bg-indigo-900/40">PPPoE</Badge>
                <code className="text-xs">keepalive-timeout=1m</code>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-indigo-100 text-indigo-800 text-xs dark:bg-indigo-900/40">Service</Badge>
                <code className="text-xs">service=ppp</code>
              </div>
              <div className="flex items-start gap-2">
                <Badge className="bg-green-100 text-green-800 text-xs dark:bg-green-900/40">✓</Badge>
                <span>Fitur baru, WireGuard, Container</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4 dark:bg-slate-900 dark:border-slate-700">
          <h4 className="font-semibold text-gray-900 mb-2 dark:text-slate-100">💡 Rekomendasi:</h4>
          <ul className="text-sm text-gray-700 space-y-1 list-disc ml-4 dark:text-slate-200">
            <li><strong>ROS 6.x:</strong> Gunakan jika butuh stabilitas maksimal dan sudah familiar</li>
            <li><strong>ROS 7.x:</strong> Gunakan untuk deployment baru dengan fitur terbaru (WireGuard, Container, better performance)</li>
            <li>Kedua versi <strong>support RADIUS dengan baik</strong>, perbedaan hanya di syntax</li>
            <li>Untuk ISP production: <strong>ROS 6.49.x Long-term</strong> atau <strong>ROS 7.x Stable</strong></li>
          </ul>
        </div>
      </div>
    </div>
    </div>
  );
};

// Troubleshooting Content
const TroubleshootingContent = ({ CodeBlock, rosVersion }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold mb-4">🔧 Troubleshooting</h2>

    {/* Version Check */}
    <Card className="border-blue-200 bg-blue-50 dark:bg-blue-900/30">
      <CardHeader>
        <CardTitle className="text-blue-900 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          Cek RouterOS Version
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm text-blue-800 mb-2">Untuk memastikan command yang tepat, cek versi RouterOS Anda:</p>
          <CodeBlock code={`/system resource print`} />
          <p className="text-sm text-blue-800 mt-2">
            Lihat baris <code className="bg-blue-100 px-2 py-0.5 rounded dark:bg-blue-900/40">version</code>:
          </p>
          <CodeBlock code={`version: 6.49.10 (stable)  ← RouterOS 6
version: 7.16 (stable)     ← RouterOS 7`} language="text" />
        </div>
      </CardContent>
    </Card>

    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-red-600 flex items-center gap-2 dark:text-red-400">
            <AlertCircle className="w-5 h-5" />
            L2TP Tidak Connect
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">1. Cek Firewall Server</h4>
            <CodeBlock code={`sudo ufw status
sudo ufw allow 1701/udp
sudo ufw allow 500/udp
sudo ufw allow 4500/udp`} />
          </div>

          <div>
            <h4 className="font-semibold mb-2">2. Cek Service Running</h4>
            <CodeBlock code={`sudo systemctl status xl2tpd
sudo systemctl status strongswan`} />
          </div>

          <div>
            <h4 className="font-semibold mb-2">3. Cek Log Server</h4>
            <CodeBlock code={`sudo tail -f /var/log/syslog | grep xl2tpd`} />
          </div>

          <div>
            <h4 className="font-semibold mb-2">4. Cek Log Mikrotik</h4>
            <CodeBlock code={`/log print where topics~"l2tp"`} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-red-600 flex items-center gap-2 dark:text-red-400">
            <AlertCircle className="w-5 h-5" />
            RADIUS Authentication Gagal
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">1. Test Ping via VPN</h4>
            <CodeBlock code={`/tool ping 10.10.10.1 count=5`} />
          </div>

          <div>
            <h4 className="font-semibold mb-2">2. Cek RADIUS Config di Mikrotik</h4>
            <CodeBlock code={`/radius print detail`} />
          </div>

          <div>
            <h4 className="font-semibold mb-2">3. Cek FreeRADIUS Log</h4>
            <CodeBlock code={`sudo tail -f /var/log/freeradius/radius.log`} />
          </div>

          <div>
            <h4 className="font-semibold mb-2">4. Test RADIUS dari Server</h4>
            <CodeBlock code={`radtest customer1 password123 localhost 0 testing123`} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-red-600 flex items-center gap-2 dark:text-red-400">
            <AlertCircle className="w-5 h-5" />
            Customer Tidak Bisa Login PPPoE
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">1. Cek User di RADIUS DB</h4>
            <CodeBlock code={`mysql -u radius -pradius_password_123 radius
SELECT * FROM radcheck WHERE username='customer1';`} />
          </div>

          <div>
            <h4 className="font-semibold mb-2">2. Cek PPPoE Server Running</h4>
            <CodeBlock code={`/interface pppoe-server server print`} />
          </div>

          <div>
            <h4 className="font-semibold mb-2">3. Monitor Live Sessions</h4>
            <CodeBlock code={`/interface pppoe-server monitor 0`} />
          </div>

          <div>
            <h4 className="font-semibold mb-2">4. Cek RADIUS dalam aplikasi</h4>
            <p className="text-sm text-gray-600 dark:text-slate-300">
              Menu: <strong>Konfigurasi MikroTik</strong> → Pilih router → Klik <strong>Test Koneksi RADIUS</strong>
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-900/30">
        <h4 className="font-semibold text-blue-900 mb-2">💡 Tips Debugging:</h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc ml-4">
          <li>Pastikan waktu server dan Mikrotik sinkron (gunakan NTP)</li>
          <li>Cek secret RADIUS sama di semua tempat (Mikrotik, FreeRADIUS clients.conf)</li>
          <li>Monitor log real-time saat customer coba login</li>
          <li>Test step-by-step: VPN dulu, baru RADIUS</li>
          <li>Gunakan Wireshark untuk capture packet RADIUS jika perlu</li>
        </ul>
      </div>
    </div>
  </div>
);

export default RADIUSGuide;
